<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            « Visite virtuelle université » figure parmi les requêtes à
            croissance record sur Google Trends — un signal clair que les
            établissements scolaires et universitaires ont compris l'enjeu. Des
            établissements qui publient des visites virtuelles signalent
            <strong
              >30 à 50 % d'inscriptions aux journées portes ouvertes en
              plus</strong
            >
            de la part d'étudiants hors région et internationaux. Voici le guide
            complet.
          </p>
        </section>

        <section>
          <h2>
            Pourquoi les établissements éducatifs ont besoin de visites
            virtuelles
          </h2>
          <p>
            Les futurs étudiants font d'abord leurs recherches en ligne avant de
            visiter en personne — ou à la place d'une visite physique. Une
            visite virtuelle convaincante du campus peut peser dans la décision
            d'un candidat à distance hésitant entre deux établissements d'égale
            réputation. La géographie n'est plus un obstacle à l'attraction des
            meilleurs profils.
          </p>
        </section>

        <section>
          <h2>
            Que capturer dans une visite virtuelle scolaire ou universitaire
          </h2>
          <p>
            Pour une visite complète, incluez : entrée principale et accueil,
            amphithéâtres et salles de cours, laboratoires et équipements
            spécialisés, espaces communs et cafétéria, bibliothèque,
            installations sportives, résidences étudiantes, espaces extérieurs
            et jardins, et le quartier environnant.
          </p>
        </section>

        <section>
          <h2>Créer une visite virtuelle avec Travvir</h2>

          <h3>Planifier le parcours</h3>
          <p>
            Cartographiez la progression naturelle d'un futur étudiant : entrée
            → accueil → espaces académiques → espaces sociaux → logements.
            Capturer dans cet ordre rend la navigation intuitive.
          </p>

          <h3>Capturer chaque espace</h3>
          <p>
            Utilisez l'application Travvir pour photographier chaque zone en
            360°. Pour les grands espaces comme les amphithéâtres, capturez
            depuis plusieurs positions pour restituer l'échelle. Pour les
            espaces plus petits, une capture centrale suffit.
          </p>

          <h3>Ajouter des hotspots d'information</h3>
          <p>
            Travvir vous permet d'ajouter des hotspots texte avec les
            informations de chaque salle — capacité, équipements, horaires,
            liens vers les pages d'inscription. Ces couches d'information
            transforment une visite visuelle en prospectus interactif.
          </p>

          <h3>Intégrer sur votre page admissions</h3>
          <p>
            Intégrez la visite directement sur votre page « Nous rejoindre » ou
            admissions. Ajoutez un appel à l'action visible (« Inscrire à une
            porte ouverte » ou « Postuler maintenant ») à proximité pour
            convertir le fort engagement généré.
          </p>
        </section>

        <section>
          <h2>Cas d'usage supplémentaires</h2>
          <ul>
            <li>
              <strong>Élèves à mobilité réduite</strong> — Permettez aux
              étudiants qui ne peuvent se déplacer de découvrir le campus.
            </li>
            <li>
              <strong>Formation des nouveaux entrants</strong> — Offrez aux
              étudiants acceptés une visite d'orientation virtuelle avant leur
              arrivée.
            </li>
            <li>
              <strong>Événements virtuels</strong> — Combinez la visite avec une
              session Zoom pour les journées portes ouvertes en ligne.
            </li>
          </ul>
        </section>
        <section class="continue-reading">
          <h2>Continuez la Lecture</h2>
          <ul>
          <li><a href="/blog/fr/visite-virtuelle-musees-galeries">Visite Virtuelle Musée et Galerie d'Art : Guide Complet 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-petites-entreprises">Visite Virtuelle pour Petites Entreprises et Commerces Locaux 2026</a></li>
          <li><a href="/blog/fr/creer-visite-virtuelle-avec-telephone">Créer une Visite Virtuelle avec son Téléphone : Guide Complet 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-google-business">Comment Ajouter une Visite Virtuelle à Google Business en 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-360-guide-complet">Visite Virtuelle 360° : Guide Complet pour Débutants (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "visite-virtuelle-ecoles-universites",
);

useBlogSeo({
  title: "Visite Virtuelle Écoles et Universités : Guide Complet | Travvir",
  description:
    "Comment créer une visite virtuelle pour votre établissement scolaire ou universitaire. Guide de capture, hotspots d'information et stratégie de publication pour augmenter les inscriptions.",
  keywords:
    "visite virtuelle école, visite virtuelle université, campus virtuel, visite virtuelle établissement scolaire",
  slug: "visite-virtuelle-ecoles-universites",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question:
        "Une visite virtuelle peut-elle remplacer une journée portes ouvertes ?",
      answer:
        "Elle la complète — les établissements qui proposent les deux enregistrent 30 à 50 % d'inscriptions aux portes ouvertes en plus de la part d'étudiants à distance.",
    },
    {
      question: "Quelles salles inclure en priorité ?",
      answer:
        "Amphithéâtres, laboratoires spécialisés, bibliothèque et résidences étudiantes — les espaces qui différencient votre offre.",
    },
  ],
});

</script>
